/**
 * Hold-to-record audio input (ENG-2135). Yields a Blob when the recording
 * stops; it knows nothing about where the Blob goes. `start()` while a
 * button is held, `stop()` on release; the hook also stops itself at
 * `maxSeconds`. Mic permission problems surface as a plain `error` string.
 */
import { useCallback, useEffect, useRef, useState } from "react";

const PREFERRED_MIME_TYPE = "audio/webm;codecs=opus";
const TICK_MS = 250;

export const MIC_UNSUPPORTED_MESSAGE =
  "Recording isn't supported in this browser.";
export const MIC_DENIED_MESSAGE =
  "Microphone access was blocked. Allow the microphone for this site in your browser and try again.";
export const MIC_NOT_FOUND_MESSAGE = "No microphone was found.";
export const MIC_FAILED_MESSAGE = "Couldn't start the microphone.";

export interface AudioRecorderOptions {
  /** Called once per recording, with the finished clip. */
  onRecorded: (audio: Blob) => void;
  /** Recording stops itself here. */
  maxSeconds: number;
}

export interface AudioRecorder {
  recording: boolean;
  /** Whole seconds since the recording started, while recording. */
  elapsedSeconds: number;
  error: string | null;
  start: () => void;
  stop: () => void;
  clearError: () => void;
}

const isSupported = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.MediaRecorder === "function" &&
  Boolean(navigator.mediaDevices?.getUserMedia);

const pickMimeType = (): string | undefined => {
  const Recorder = window.MediaRecorder as
    | (typeof MediaRecorder & { isTypeSupported?: (t: string) => boolean })
    | undefined;
  if (Recorder?.isTypeSupported?.(PREFERRED_MIME_TYPE)) {
    return PREFERRED_MIME_TYPE;
  }
  return undefined;
};

const describeMicError = (reason: unknown): string => {
  const name = (reason as { name?: string } | null)?.name ?? "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return MIC_DENIED_MESSAGE;
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return MIC_NOT_FOUND_MESSAGE;
  }
  return MIC_FAILED_MESSAGE;
};

export const useAudioRecorder = ({
  onRecorded,
  maxSeconds,
}: AudioRecorderOptions): AudioRecorder => {
  const [recording, setRecording] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // True between start() and stop(); a release before getUserMedia resolves
  // must not start a recording nobody is holding.
  const wantRecordingRef = useRef(false);
  const onRecordedRef = useRef(onRecorded);
  onRecordedRef.current = onRecorded;

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const stopTicking = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    wantRecordingRef.current = false;
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
      return;
    }
    // Not recording (yet): nothing to flush, just tidy up.
    stopTicking();
    releaseStream();
    setRecording(false);
  }, [releaseStream, stopTicking]);

  const start = useCallback(() => {
    if (wantRecordingRef.current || recorderRef.current) return;
    setError(null);
    if (!isSupported()) {
      setError(MIC_UNSUPPORTED_MESSAGE);
      return;
    }
    wantRecordingRef.current = true;

    navigator.mediaDevices.getUserMedia({ audio: true }).then(
      (stream) => {
        if (!wantRecordingRef.current) {
          // Released before the mic came up.
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        chunksRef.current = [];
        const mimeType = pickMimeType();
        let recorder: MediaRecorder;
        try {
          recorder = mimeType
            ? new MediaRecorder(stream, { mimeType })
            : new MediaRecorder(stream);
        } catch (reason) {
          wantRecordingRef.current = false;
          releaseStream();
          setError(describeMicError(reason));
          return;
        }
        recorder.ondataavailable = (event: BlobEvent) => {
          if (event.data && event.data.size > 0) {
            chunksRef.current.push(event.data);
          }
        };
        recorder.onstop = () => {
          const type = recorder.mimeType || mimeType || "audio/webm";
          const audio = new Blob(chunksRef.current, { type });
          chunksRef.current = [];
          recorderRef.current = null;
          stopTicking();
          releaseStream();
          setRecording(false);
          onRecordedRef.current(audio);
        };
        recorder.onerror = () => {
          setError(MIC_FAILED_MESSAGE);
          stop();
        };
        recorderRef.current = recorder;
        recorder.start();
        const startedAt = Date.now();
        setElapsedSeconds(0);
        setRecording(true);
        stopTicking();
        tickRef.current = setInterval(() => {
          const elapsed = Math.floor((Date.now() - startedAt) / 1000);
          setElapsedSeconds(elapsed);
          if (elapsed >= maxSeconds) stop();
        }, TICK_MS);
      },
      (reason) => {
        wantRecordingRef.current = false;
        setError(describeMicError(reason));
      }
    );
  }, [maxSeconds, releaseStream, stop, stopTicking]);

  useEffect(
    () => () => {
      // Unmount mid-hold: drop the recording rather than send it.
      wantRecordingRef.current = false;
      const recorder = recorderRef.current;
      recorderRef.current = null;
      if (recorder && recorder.state !== "inactive") {
        recorder.onstop = null;
        try {
          recorder.stop();
        } catch {
          // Already stopped.
        }
      }
      stopTicking();
      releaseStream();
    },
    [releaseStream, stopTicking]
  );

  const clearError = useCallback(() => setError(null), []);

  return { recording, elapsedSeconds, error, start, stop, clearError };
};
